import DispatchContent from "../../features/planning/dailyDispatch/DispatchContent";
import JobReport from "../../features/planning/dailyDispatch/DailyJobReport/JobReport";
import DispatchScopeActionMenu from "../../features/planning/dailyDispatch/shared/DispatchScopeActionMenu";
import { useEffect } from "react";

import DatesBar from "../../features/planning/dailyDispatch/DatesBar";
import TopBar from "../../features/planning/dailyDispatch/TopBar";

import WorkerSelectModal from "../../features/planning/shared/WorkerSelectModal";
import { Alert } from "../../components/ui";
import { ConfirmModal } from "../../components/ui";

import {
  DispatchProvider,
  useDispatchContext,
} from "../../context/domain/planning/DispatchContext";
import useCaptureCtx, { CaptureContext } from "../../context/domain/planning/CaptureContext";
import { usePlanningContext } from "../../context/domain/planning/PlanningContext";
import useWeeklyCtx, { WeeklyContext, useWeeklyContext } from "../../context/global/WeeklyContext";
import {
  formatShortMonthDay,
  getDateInLocalTZFromDateStr,
} from "../../utils/datetime/core";

import { handleKeyPress } from "../../utils/dom";

/**
 * Inner component that uses both Weekly and Dispatch contexts.
 * This must be inside the WeeklyContext and DispatchProvider.
 *
 * @param {Object} props
 * @param {boolean} [props.compact=false] Whether compact chrome is enabled.
 * @param {boolean} [props.embedded=false] Only affects title bar background (and document.title).
 * @param {string} [props.title="R&S Dispatch"] Page heading.
 * @returns {JSX.Element}
 */
const DispatchInner = ({
  compact = false,
  embedded = false,
  title = "R&S Dispatch",
}) => {
  const { jobs, showWeekends } = useWeeklyContext();
  const { planningLastUpdated } = usePlanningContext();
  const {
    isWorkerModalOpen,
    closeWorkerModal,
    modalProps,
    isDispatchCapturing,
    isJobReportCapturing,
    setCopyScopeOrigin,
    setCopyScopesDate,
    workerUnskipConflictModal,
    confirmWorkerUnskipConflictModal,
    cancelWorkerUnskipConflictModal,
    scopeActionMenu,
    compact: contextCompact,
  } = useDispatchContext();
  const isCompact = compact || contextCompact;
  // Capture context is used for setting the state and vars of the capture. The capture
  // has different requirements than the main dispatch so it has its own context.
  const captureCtx = useCaptureCtx();

  useEffect(() => {
    if (!embedded) {
      document.title = title;
    }
  }, [embedded, title]);

  useEffect(() => {
    const cleanup = handleKeyPress("Escape", () => {
      setCopyScopeOrigin(null);
      setCopyScopesDate(null);
    });

    return cleanup;
  }, [setCopyScopeOrigin, setCopyScopesDate]);

  // Title bg is the only layout difference for `embedded` (title text is a prop).
  const titleRow = (
    <div
      className="flex items-center justify-between gap-4 px-4 py-2 bg-gray-200"
    >
      <h1 className="text-2xl font-bold text-gray-950">{title}</h1>
      <div className="truncate text-right text-xs text-gray-600">
        {planningLastUpdated}
      </div>
    </div>
  );

  // TopBar + DatesBar always sit above content (full-width stacked DatesBar).
  // Sticky under NavBar on the full page; sticky at card top when compact.
  const dispatchBody = (
    <>
      {titleRow}
      <div className={`z-20 ${isCompact ? "sticky top-0" : "sticky top-14"}`}>
        <TopBar />
        <DatesBar layout="stacked" />
      </div>
      <DispatchContent hideDatesBar />
    </>
  );

  // Interaction modals are shared by full and compact embeds (crew edits, conflicts, etc.).
  const interactionModals = (
    <>
      {isWorkerModalOpen && (
        <WorkerSelectModal
          {...modalProps}
          isOpen={isWorkerModalOpen}
          onClose={closeWorkerModal}
        />
      )}
      {scopeActionMenu ? (
        <div
          className="fixed z-[10030] h-0 w-0"
          style={{
            left: scopeActionMenu.anchorRect?.right ?? 0,
            top: scopeActionMenu.anchorRect?.top ?? 0,
          }}
        >
          <DispatchScopeActionMenu
            scope={scopeActionMenu.scope}
            date={scopeActionMenu.date}
            align="right"
            showButton={false}
            openKey={scopeActionMenu.openKey}
            disableCopy={scopeActionMenu.disableCopy}
          />
        </div>
      ) : null}
      <ConfirmModal
        isOpen={workerUnskipConflictModal != null}
        title="Worker Conflict Detected"
        buttonText="Confirm"
        onConfirm={confirmWorkerUnskipConflictModal}
        onClose={cancelWorkerUnskipConflictModal}
      >
        <div className="mx-auto max-w-md space-y-2 text-left">
          {(workerUnskipConflictModal ?? []).map(({ dateKey, conflictNames }) => (
            <Alert
              key={dateKey}
              type="warning"
              message={`${formatShortMonthDay(
                getDateInLocalTZFromDateStr(dateKey)
              )}: ${conflictNames.join(", ")}`}
            />
          ))}
          <div className="pt-2 text-center text-gray-600">
            This unskipped scope will be assigned an updated crew without workers
            already assigned or marked off work for this day.
          </div>
        </div>
      </ConfirmModal>
    </>
  );

  const weekMinWidthClass = showWeekends
    ? "min-w-dispatchWeekends"
    : "min-w-dispatch";

  // Compact (project embed): fill the card. Full page: min-height shell + capture clones.
  if (isCompact) {
    return (
      <>
        <div
          className={`font-inter w-full cursor-default caret-transparent bg-white ${weekMinWidthClass}`}
        >
          <div className="flex w-full flex-col">{dispatchBody}</div>
        </div>
        {interactionModals}
      </>
    );
  }

  return (
    <div
      className={`font-inter min-h-screen w-full cursor-default caret-transparent bg-custom-rsbg ${weekMinWidthClass}`}
    >
      <div className="flex min-h-screen w-full flex-col">{dispatchBody}</div>
      {/* Copy of dispatch content used for capturing */}
      <CaptureContext.Provider value={captureCtx}>
        {isDispatchCapturing && (
          <DispatchContent className="hidden dispatch-content-to-capture" />
        )}
        {isJobReportCapturing &&
          jobs &&
          jobs.map((job) => (
            <JobReport
              key={job.id}
              className={`${job.number} jobreport-content-to-capture`}
              job={job}
            />
          ))}
      </CaptureContext.Provider>
      {interactionModals}
    </div>
  );
};

/**
 * Entry point for the Daily Dispatch page and Project embeds.
 *
 * @component
 * @param {Object} [props]
 * @param {boolean} [props.compact=false] Whether to use compact dispatch chrome.
 * @param {boolean} [props.readOnly=false] Whether mutation UI/actions are disabled.
 * @param {Object|null} [props.singleProject=null] Optional project used to scope dispatch data.
 * @param {boolean} [props.detailsOnly=false] Whether to lock jobs into details view.
 * @param {boolean} [props.loadDayDetails] Whether to load DayDetails (locks/stages). Defaults true.
 * @param {boolean} [props.loadWeather] Whether to load weather. Defaults to !compact.
 * @param {Date|null} [props.initialDate=null] Optional week-seed date for embeds.
 * @param {boolean} [props.disableUrlSync=false] Whether week navigation updates local state only.
 * @param {number|null} [props.startOfWeek=null] Optional week-start day override.
 * @param {boolean} [props.embedded=false] Only affects title bar background (and document.title).
 * @param {string} [props.title="R&S Dispatch"] Page heading; override when embedding.
 * @returns {JSX.Element}
 */
const DailyDispatch = ({
  compact = false,
  readOnly = false,
  singleProject = null,
  detailsOnly = false,
  loadDayDetails,
  loadWeather,
  initialDate = null,
  disableUrlSync = false,
  startOfWeek = null,
  embedded = false,
  title = "R&S Dispatch",
} = {}) => {
  const weeklyCtx = useWeeklyCtx({
    initialDate,
    disableUrlSync,
    startOfWeek,
  });

  return (
    <WeeklyContext.Provider value={weeklyCtx}>
      <DispatchProvider
        compact={compact}
        readOnly={readOnly}
        singleProject={singleProject}
        detailsOnly={detailsOnly}
        options={{
          ...(loadDayDetails === undefined ? {} : { loadDayDetails }),
          ...(loadWeather === undefined ? {} : { loadWeather }),
        }}
      >
        <DispatchInner
          compact={compact}
          embedded={embedded}
          title={title}
        />
      </DispatchProvider>
    </WeeklyContext.Provider>
  );
};

export default DailyDispatch;
